import Vue from 'vue';
import Router from 'vue-router';
import URLS from './URLS';
import AuthService from './services/AuthService';
import AnalyticService from './services/AnalyticService';
import RestX from 'highered-utils/restx';
import SchoolService from './services/SchoolService';
import DialogService from './services/DialogService';

// Configure feature toggles
import FeatureToggles from 'highered-utils/feature-toggle';
import buildConfig from './config/config.json';
import CustomProperties from './utils/CustomProperties';
FeatureToggles.useToggles(buildConfig['toggles']);
Vue.use(FeatureToggles);
CustomProperties.setCustomProperties(buildConfig['customProperties']);
Vue.use(CustomProperties);

import i18n from '~/i18n';
import localization from './localization';

import platformConfig from '~/config/config';

Vue.use(Router);

export const IS_PROD = Boolean(window.location.host.match(/\.gethighered\./));

export const SAAS_DISABLED = !FeatureToggles.toggle('saas');

// ROUTE TOGGLES
export const SHOW_JOBS = localization.isSchoolPlatform && FeatureToggles.toggle('pages.findJobs');
export const SHOW_COMPANIES =
    localization.isSchoolPlatform && FeatureToggles.toggle('pages.findCompanies');
export const HAS_LOGIN = FeatureToggles.toggle('logIn.logIn');
export const HAS_FORCED_LOGIN =
    localization.isSchoolPlatform && FeatureToggles.toggle('logIn.forcedLogin');

export const HAS_MY_PAGE =
    localization.isSchoolPlatform && HAS_LOGIN && FeatureToggles.toggle('pages.myPage');
export const HAS_MY_PROFILE = HAS_LOGIN && FeatureToggles.toggle('pages.myProfile');
export const HAS_FAVOURITES =
    localization.isSchoolPlatform && FeatureToggles.toggle('pages.favourites');
export const HAS_ASSESSMENTS =
    localization.isSchoolPlatform && HAS_LOGIN && FeatureToggles.toggle('pages.assessments');
export const HAS_COURSERA =
    localization.isSchoolPlatform &&
    HAS_LOGIN &&
    !SAAS_DISABLED &&
    FeatureToggles.toggle('pages.coursera');
export const HAS_MASTERCLASSES =
    localization.isSchoolPlatform && HAS_LOGIN && FeatureToggles.toggle('pages.masterClasses');
export const HAS_CALENDAR =
    localization.isSchoolPlatform && HAS_LOGIN && FeatureToggles.toggle('pages.calendar');
export const HAS_INTERNAL_RESOURCES =
    localization.isSchoolPlatform && HAS_LOGIN && FeatureToggles.toggle('pages.internalResources');
export const HAS_COUNTRY_GUIDES =
    localization.isSchoolPlatform && HAS_LOGIN && FeatureToggles.toggle('pages.countryGuides');
export const HAS_HAIGUI_LINK =
    localization.isSchoolPlatform && HAS_LOGIN && FeatureToggles.toggle('pages.haiguiLink');

export const HAS_REFER_A_FRIEND =
    false &&
    localization.isSchoolPlatform &&
    HAS_LOGIN &&
    FeatureToggles.toggle('pages.referAFriend');
export const HAS_DOCUMENTS = platformConfig.hasPage('documents', true);

export const PROMOTED_RESOURCE = FeatureToggles.toggle('promotedResource');

export const SHOW_PROFILE_BANNER = HAS_LOGIN && FeatureToggles.toggle('logIn.profileBanner');
export const SHOW_UNIVERSITY_BADGES =
    localization.isSchoolPlatform && HAS_LOGIN && FeatureToggles.toggle('logIn.universityBadges');
export const PROFILE_MODULE = HAS_LOGIN && FeatureToggles.toggle('logIn.profileModule');
export const FEATURE_COMPANIES_MODULE = FeatureToggles.toggle('companies.featuredModule');
export const FEATURE_POSITIONS_MODULE = FeatureToggles.toggle('positions.featuredModule');
export const FEATURE_POSITIONS_IN_LIST = FeatureToggles.toggle('positions.featured');
export const FORCE_VERIFIED_APPLICATION_CLICK =
    HAS_LOGIN && FeatureToggles.toggle('verify.jobApply');

export const VERIFY_MODULE = HAS_LOGIN && FeatureToggles.toggle('verify.verifyModule');

export const REQUIRE_VERIFICATION_BADGE_PROMPT =
    HAS_LOGIN && FeatureToggles.toggle('verify.badgePrompt');

//Masterclass-related verification requirements
const VERIFY_MASTERCLASS =
    HAS_LOGIN && FeatureToggles.toggle('verify.requireVerification.resources');

//Internal-resource-related verification requirements
const VERIFY_INTERNAL_RESOURCES_OVERVIEW =
    HAS_LOGIN && FeatureToggles.toggle('verify.requireVerification.internalResources.overview');

const VERIFY_INTERNAL_RESOURCES_FOLDER =
    HAS_LOGIN && FeatureToggles.toggle('verify.requireVerification.internalResources.folder');

const VERIFY_INTERNAL_RESOURCES_PARENT =
    VERIFY_INTERNAL_RESOURCES_OVERVIEW &&
    VERIFY_INTERNAL_RESOURCES_FOLDER &&
    FeatureToggles.toggle('verify.requireVerification.internalResources');

//Calendar-related verification requirements
const VERIFY_CALENDAR_OVERVIEW =
    HAS_LOGIN && FeatureToggles.toggle('verify.requireVerification.calendar.overview');

const VERIFY_CALENDAR_BOOK =
    HAS_LOGIN && FeatureToggles.toggle('verify.requireVerification.calendar.appointmentList');

const VERIFY_CALENDAR_APPOINTMENT_BOOK =
    HAS_LOGIN && FeatureToggles.toggle('verify.requireVerification.calendar.appointmentBook');

const VERIFY_CALENDAR_PARENT =
    VERIFY_CALENDAR_OVERVIEW &&
    VERIFY_CALENDAR_BOOK &&
    VERIFY_CALENDAR_APPOINTMENT_BOOK &&
    FeatureToggles.toggle('verify.requireVerification.calendar');

// Full parent verification requirement
export const REQUIRE_VERIFICATION =
    VERIFY_INTERNAL_RESOURCES_PARENT &&
    VERIFY_CALENDAR_PARENT &&
    VERIFY_MASTERCLASS &&
    FeatureToggles.toggle('verify.requireVerification');

export const HAS_VFAIRS = true; //!FeatureToggles.toggle('beta.vfairs');

const isMobile = () => {
    return (
        navigator &&
        /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    );
}; // Temp hack to avoid chat showing on mobile until its properly setup.

export const HAS_CHAT = !isMobile() && FeatureToggles.toggle('chat.enabled');

export const SHOW_CHAT_IN_COMPANY_LIST =
    HAS_CHAT && FeatureToggles.toggle('chat.availability.companyList');
export const SHOW_CHAT_IN_COMPANY_PROFILE =
    HAS_CHAT && FeatureToggles.toggle('chat.availability.companyProfile');

const homeRoute = localization.isCompanyPlatform
    ? {
          path: '/',
          name: 'home',
          component: () => import('./views/home/Home'),
      }
    : {
          path: URLS.findjobs,
          name: 'jobs',
          component: () => import('./views/findjobs/FindJobs'),
      };

const redirectVFairRoute = localization.isCompanyPlatform
    ? []
    : [
          {
              path: '/',
              redirect: URLS.findjobs,
          },
      ];

let routes = [].concat(
    [
        {
            path: URLS.mypage,
            name: 'mypage',
            component: () => import('./views/mypage/MyPageOld'),
            meta: { requiresAuth: true },
            toggle: HAS_MY_PAGE,
        },
    ],
    redirectVFairRoute,
    [
        homeRoute,
        {
            path: URLS.vfairs,
            name: 'vfairs',
            component: () => import('./views/vfairs/list/FindVFairs'),
        },
        {
            path: URLS.jobDetail.concat('/:id'),
            name: 'jobdetail',
            component: () => import('./views/jobdetail/JobDetail'),
        },
        {
            path: URLS.resources,
            name: 'resources',
            component: () => import('./views/resources/Resources'),
            meta: {
                requiresAuth: true,
                requiresVerification: VERIFY_MASTERCLASS,
            },
            toggle: HAS_MASTERCLASSES,
        },
        {
            path: URLS.internalResources,
            name: 'internalresources',
            component: () => import('./views/resources_internal/overview'),
            meta: {
                requiresAuth: true,
                requiresVerification: VERIFY_INTERNAL_RESOURCES_OVERVIEW,
            },
            toggle: HAS_MY_PAGE && HAS_INTERNAL_RESOURCES && VERIFY_INTERNAL_RESOURCES_OVERVIEW,
        },
        {
            path: URLS.internalResources.concat('/:id'),
            name: 'internalresources-folder',
            component: () => import('./views/resources_internal/folder'),
            meta: {
                requiresAuth: true,
                requiresVerification: VERIFY_INTERNAL_RESOURCES_FOLDER,
            },
            toggle: HAS_MY_PAGE && HAS_INTERNAL_RESOURCES && VERIFY_INTERNAL_RESOURCES_FOLDER,
        },
        {
            path: URLS.calendar,
            name: 'calendar-overview',
            component: () => import('./views/calendar/overview'),
            meta: {
                requiresAuth: true,
                requiresVerification: VERIFY_CALENDAR_OVERVIEW,
            },
            toggle: HAS_MY_PAGE && HAS_CALENDAR && VERIFY_CALENDAR_OVERVIEW,
        },
        {
            path: URLS.documents,
            name: 'documents-overview',
            component: () => import('./views/documents/Documents'),
            meta: {
                requiresAuth: true,
                requiresVerification: true,
            },
            toggle: HAS_DOCUMENTS,
        },
        {
            path: URLS.cvs,
            name: 'cv-overview',
            component: () => import('./views/documents/Documents'),
            meta: {
                requiresAuth: true,
                requiresVerification: true,
            },
            toggle: HAS_DOCUMENTS,
        },
        {
            path: URLS.referrals,
            name: 'referrals-overview',
            component: () => import('./views/referrals/Referrals'),
            meta: {
                requiresAuth: true,
                requiresVerification: true,
            },
            beforeEnter: (to, from, next) => {
                RestX.getInterfaces()
                    .user.me.get()
                    .then(user => {
                        if (user.data.firstName && user.data.lastName) {
                            next();
                        } else {
                            DialogService.openAddNameDialog(router);
                        }
                    });
            },
            toggle: HAS_MY_PAGE && HAS_REFER_A_FRIEND,
        },
        {
            path: URLS.calendar.concat(URLS.book),
            name: 'calendar-appointments-list',
            component: () => import('./views/calendar/appointment_list'),
            meta: {
                requiresAuth: true,
                requiresVerification: VERIFY_CALENDAR_BOOK,
            },
            toggle: HAS_MY_PAGE && HAS_CALENDAR && VERIFY_CALENDAR_BOOK,
        },
        {
            path: URLS.plansAndBilling,
            name: 'plans-and-billing',
            component: () => import('./views/plansandbilling/PlansAndBilling'),
            meta: {
                requiresAuth: true,
            },
            toggle: HAS_MY_PAGE && !SAAS_DISABLED,
        },
        {
            path: URLS.calendar.concat(URLS.book).concat('/:id'),
            name: 'calendar-appointments-book',
            component: () => import('./views/calendar/appointment_book'),
            meta: {
                requiresAuth: true,
                requiresVerification: VERIFY_CALENDAR_APPOINTMENT_BOOK,
            },
            toggle: HAS_MY_PAGE && HAS_CALENDAR && VERIFY_CALENDAR_APPOINTMENT_BOOK,
        },
        {
            path: URLS.findcompanies,
            name: 'companies',
            component: () => import('./views/findcompanies/FindCompanies'),
        },
        {
            path: URLS.findcompanies.concat('/:id'),
            name: 'companydetail',
            component: () => import('./views/companydetail/CompanyDetail'),
        },
        // {
        //     path: URLS.spotlight,
        //     name: 'spotlight',
        //     component: () => import('./views/spotlight/Spotlight'),
        //     meta: { requiresAuth: true },
        //     toggle: HAS_MY_PAGE || HAS_MY_PROFILE,
        // },
        {
            path: URLS.profile,
            name: 'profile',
            component: () => import('./views/profile/Profile'),
            meta: { requiresAuth: true },
            toggle: HAS_MY_PAGE || HAS_MY_PROFILE,
        },
        {
            path: URLS.profileDocuments,
            name: 'profile-documents',
            component: () => import('./views/profile/ProfileDocuments'),
            meta: { requiresAuth: true },
            toggle: HAS_MY_PAGE || HAS_MY_PROFILE,
        },
        {
            path: URLS.favorites,
            name: 'favorites',
            component: () => import('./views/favorites/Favorites'),
            meta: { requiresAuth: true },
            toggle: HAS_MY_PAGE,
        },
        {
            path: URLS.assessments,
            name: 'assessments-landing',
            redirect: URLS.assessments.concat(URLS.assessmentCenter),
        },
        {
            path: URLS.careerPlanning,
            name: 'career-planning-landing',
            redirect: URLS.assessments.concat(URLS.assessmentCenter),
        },
        {
            path: URLS.assessments.concat(URLS.assessmentsRedirect),
            name: 'assessments-redirect',
            redirect: URLS.assessments.concat(URLS.assessmentCenter),
        },
        {
            path: URLS.assessments.concat(URLS.assessmentCenter),
            name: 'assessments-center',
            component: () => import('./views/assessmentscenter/AssessmentsCenter'),
            meta: { requiresAuth: true },
            toggle: HAS_ASSESSMENTS,
        },
        {
            path: URLS.tests.concat(URLS.cute),
            name: 'legacy-tests-cute',
            redirect: { name: 'assessments-center' },
        },
        {
            path: URLS.tests.concat('*'),
            name: 'legacy-tests',
            redirect: { name: 'assessments-landing' },
        },

        /* Coursera */
        {
            path: URLS.coursera,
            name: 'coursera-landing',
            component: () => import('./views/coursera/CourseraLanding'),
            meta: { requiresAuth: true },
            toggle: HAS_COURSERA,
        },
        {
            path: URLS.courseraCatalog,
            name: 'coursera-catalog',
            component: () => import('./views/coursera/CourseraLanding'),
            meta: { requiresAuth: true },
            toggle: HAS_COURSERA,
        },
        {
            path: URLS.courseraMyCourses,
            name: 'coursera-my-courses',
            component: () => import('./views/coursera/CourseraLanding'),
            meta: { requiresAuth: true },
            toggle: HAS_COURSERA,
        },
        {
            path: URLS.courseraChooseSubscription,
            name: 'coursera-choose-subscription',
            component: () => import('./views/coursera/payments/ChooseSubscription'),
            meta: { requiresAuth: true },
            toggle: HAS_COURSERA,
        },
        {
            path: URLS.courseraHowTo,
            name: 'coursera-how-to',
            component: () => import('./views/coursera/payments/HowTo'),
            meta: { requiresAuth: true },
            toggle: HAS_COURSERA,
        },
        {
            path: '/coursera',
            redirect: () => URLS.coursera,
        },

        {
            path: URLS.emailVerification,
            name: 'emailVerification',
            component: () => import('./views/emailverification/EmailVerification'),
        },
        {
            path: URLS.recoverUser,
            name: 'recoverUser',
            component: () => import('./views/recoveruser/RecoverUser'),
        },
        {
            path: URLS.recoverPassword,
            name: 'recoverPassword',
            component: () => import('./views/recoverpassword/RecoverPassword'),
        },
        {
            path: URLS.redirect, // Is for handling redirection-events, for example oauth-flow
            name: 'redirect',
            component: () => import('./views/redirect/Redirect'),
        },
        {
            path: URLS.aboutGPTW,
            name: 'aboutGPTW',
            component: () => import('./views/aboutGPTW/AboutGPTW'),
            toggle: !IS_PROD,
        },
        {
            path: URLS.activateUser,
            name: 'activateUser',
            component: () => import('./views/activateuser/ActivateUser'),
        },
        {
            // Fallback for previous vfair urls, can be removed later
            path: URLS.vfairsLegacy,
            redirect: URLS.vfairs,
        },
        {
            // Fallback for previous vfair urls, can be removed later
            path: URLS.vfairLegacy.concat('/:id'),
            redirect: URLS.vfair.concat('/:id'),
        },
        {
            path: URLS.vfair.concat('/:id'),
            name: 'vfair',
            component: () => import('./views/vfairs/vfair/VFair'),
            meta: {
                requiresAuth: true,
                requiresVerification:
                    localization.isSchoolPlatform &&
                    FeatureToggles.toggle('verify.requireVerification.vfair'),
            },
            children: [
                {
                    // Fallback for previous vfair urls, can be removed later
                    path: URLS.vfairLegacy.concat('/:id'),
                    redirect: URLS.vfair.concat('/:id').concat('/booths'),
                },
                {
                    // Redirect root to booths
                    path: URLS.vfair.concat('/:id'),
                    redirect: URLS.vfair.concat('/:id').concat('/booths'),
                },
                // Booths
                {
                    path: URLS.vfair.concat('/:id').concat('/booths'),
                    name: 'vfair-booths',
                    component: () => import('./views/vfairs/vfair/Booths'),
                    requiresVerification: FeatureToggles.toggle('verify.requireVerification.vfair'),
                },
                {
                    path: URLS.vfair
                        .concat('/:id')
                        .concat('/booths')
                        .concat('/:boothId'),
                    name: 'vfair-booth',
                    component: () => import('./views/vfairs/vfair/Booth'),
                    requiresVerification: FeatureToggles.toggle('verify.requireVerification.vfair'),
                },
                // Jobs
                {
                    // Fallback for previous vfair urls, can be removed later
                    path: URLS.vfairLegacy.concat('/:id').concat('/jobs'),
                    redirect: URLS.vfair.concat('/:id').concat('/jobs'),
                },
                {
                    path: URLS.vfair.concat('/:id').concat('/jobs'),
                    name: 'vfair-jobs',
                    component: () => import('./views/vfairs/vfair/Jobs'),
                    requiresVerification: FeatureToggles.toggle('verify.requireVerification.vfair'),
                    children: [
                        {
                            // Fallback for previous vfair urls, can be removed later
                            path: URLS.vfairLegacy
                                .concat('/:id')
                                .concat('/jobs')
                                .concat('/:jobId'),
                            redirect: URLS.vfair
                                .concat('/:id')
                                .concat('/jobs')
                                .concat('/:jobId'),
                        },
                        {
                            path: URLS.vfair
                                .concat('/:id')
                                .concat('/jobs')
                                .concat('/:jobId'),
                            name: 'vfair-job',
                            component: () => import('./views/vfairs/vfair/Job'),
                            requiresVerification: FeatureToggles.toggle(
                                'verify.requireVerification.vfair'
                            ),
                        },
                    ],
                },
                // Webinars
                {
                    // Fallback for previous vfair urls, can be removed later
                    path: URLS.vfairLegacy.concat('/:id').concat('/events'),
                    redirect: URLS.vfair.concat('/:id').concat('/webinars'),
                },
                {
                    // Fallback for previous vfair urls, can be removed later
                    path: URLS.vfair.concat('/:id').concat('/events'),
                    redirect: URLS.vfair.concat('/:id').concat('/webinars'),
                },
                {
                    path: URLS.vfair.concat('/:id').concat('/webinars'),
                    name: 'vfair-webinars',
                    component: () => import('./views/vfairs/vfair/Webinars'),
                    requiresVerification: FeatureToggles.toggle('verify.requireVerification.vfair'),
                    children: [
                        {
                            // Fallback for previous vfair urls, can be removed later
                            path: URLS.vfairLegacy
                                .concat('/:id')
                                .concat('/webinars')
                                .concat('/:webinarId'),
                            redirect: URLS.vfair
                                .concat('/:id')
                                .concat('/webinars')
                                .concat('/:webinarId'),
                        },
                        {
                            path: URLS.vfair
                                .concat('/:id')
                                .concat('/webinars')
                                .concat('/:webinarId'),
                            name: 'vfair-webinar',
                            component: () => import('./views/vfairs/vfair/Webinar'),
                            requiresVerification: FeatureToggles.toggle(
                                'verify.requireVerification.vfair'
                            ),
                        },
                    ],
                },
                // Chat
                {
                    path: URLS.vfair.concat('/:id').concat('/chat'),
                    name: 'vfair-chat',
                    component: () => import('./views/vfairs/vfair/Chat'),
                    requiresVerification: FeatureToggles.toggle('verify.requireVerification.vfair'),
                },
                // Conference
                {
                    path: URLS.vfair.concat('/:id').concat('/conference'),
                    name: 'vfair-conference',
                    component: () => import('./views/vfairs/vfair/Conference.vue'),
                    requiresVerification: FeatureToggles.toggle('verify.requireVerification.vfair'),
                },

                /* Old */
                // {
                //     path: URLS.vfair.concat('/:id').concat('/'),
                //     name: 'vfair-home',
                //     component: () => import('./views/vfairs/vfair/Home'),
                //     requiresVerification: FeatureToggles.toggle('verify.requireVerification.vfair'),
                // },
                // {
                //     // Fallback for previous vfair urls, can be removed later
                //     path: URLS.vfairLegacy.concat('/:id').concat('/companies'),
                //     redirect: URLS.vfair.concat('/:id').concat('/companies'),
                // },
                // {
                //     path: URLS.vfair.concat('/:id').concat('/companies'),
                //     name: 'vfair-companies',
                //     component: () => import('./views/vfairs/vfair/Companies'),
                //     requiresVerification: FeatureToggles.toggle('verify.requireVerification.vfair'),
                //     children: [
                //         {
                //             // Fallback for previous vfair urls, can be removed later
                //             path: URLS.vfairLegacy
                //                 .concat('/:id')
                //                 .concat('/companies')
                //                 .concat('/:companyId'),
                //             redirect: URLS.vfair
                //                 .concat('/:id')
                //                 .concat('/companies')
                //                 .concat('/:companyId'),
                //         },
                //         {
                //             path: URLS.vfair
                //                 .concat('/:id')
                //                 .concat('/companies')
                //                 .concat('/:companyId'),
                //             name: 'vfair-company',
                //             component: () => import('./views/vfairs/vfair/Company'),
                //             requiresVerification: FeatureToggles.toggle(
                //                 'verify.requireVerification.vfair'
                //             ),
                //         },
                //     ],
                // },
                // {
                //     path: URLS.vfair.concat('/:id').concat('/articles'),
                //     name: 'vfair-articles',
                //     component: () => import('./views/vfairs/vfair/Articles'),
                //     requiresVerification: FeatureToggles.toggle('verify.requireVerification.vfair'),
                // },
            ],
        },
        {
            // Fallback for previous vfair urls, can be removed later
            path: URLS.vfairsLegacy.concat('/:id'),
            redirect: URLS.vfairs.concat('/:id'),
        },
        {
            path: URLS.vfairs.concat('/:id'),
            name: 'vfairdetails',
            component: () => import('./views/vfairs/details/VFairDetails'),
            meta: {
                requiresAuth: localization.isSchoolPlatform,
                requiresVerification:
                    localization.isSchoolPlatform &&
                    FeatureToggles.toggle('verify.requireVerification.vfair'),
            },
        },
        {
            path: URLS.schoolEvents,
            name: 'schoolevents',
            component: () => import('./views/schoolevents/SchoolEvents'),
        },
        {
            path: URLS.schoolEvents.concat('/:id'),
            name: 'schooleventpage',
            component: () => import('./views/schoolevents/SchoolEventPage'),
        },
        {
            path: '*',
            name: 'pagenotfound',
            component: () => import('./views/pagenotfound/PageNotFound'),
        },
    ]
);

// Remove routes that has been toggled off
routes = routes.filter(r => (typeof r.toggle === 'undefined' ? true : r.toggle));

export function getVFairEventRoute(vFairId, eventId) {
    return URLS.vfair
        .concat('/')
        .concat(vFairId)
        .concat('/webinars')
        .concat('/')
        .concat(eventId);
}

const router = new Router({
    base: '/', // process.env.BASE_URL,
    mode: 'history',
    scrollBehavior(/*to, from, savedPosition*/) {
        // Check
        return { x: 0, y: 0 };
    },
    routes: routes,
});

router.beforeEach((to, from, next) => {
    const logInModule = RestX.getInterfaces().logInModule;
    logInModule.resetDisabledPopup();
    // Check if user is trying to leave spotlight-page with unsaved changes
    if (from.path === URLS.spotlight && AuthService.isLoggedIn()) {
        if (RestX.getInterfaces().unsavedChanges.hasUnsavedChanges()) {
            RestX.getInterfaces().unsavedChanges.setRedirect(to.path);
            RestX.getInterfaces().unsavedChanges.toggleOpen();
        } else {
            next();
        }
    } else if (to.matched.some(record => record.meta.requiresAuth)) {
        // Check if authenticated (there is a token)
        if (!AuthService.isLoggedIn()) {
            RestX.getInterfaces().logInModule.toggleOpen();
            RestX.getInterfaces().logInModule.setRedirect(to.path);
            RestX.getInterfaces().logInModule.setThen(() => next());
        } else {
            if (
                to.matched.some(
                    record =>
                        record.meta.requiresVerification &&
                        FeatureToggles.toggle('verify.requireVerification')
                )
            ) {
                const currentUniDomain = SchoolService.getCurrentSchoolDomain();
                RestX.getInterfaces()
                    .university.connection.get({})
                    .then(it => {
                        let currentUni = it.data.find(
                            c => (c.school.subDomain || '').toLowerCase() === currentUniDomain
                        );
                        if (currentUni) {
                            // User either is verified at this school or has a pending verification
                            if (currentUni.isVerified) {
                                next();
                            } else {
                                DialogService.openUniVerifyDialog({
                                    payload: {
                                        checkConnections: true,
                                        helperText: i18n.t(
                                            'general.dialogs.helperTexts.checkHasPendingAction'
                                        ),
                                    },
                                    then: payload => {
                                        if (payload instanceof Object && payload.isVerified) {
                                            next();
                                        }
                                    },
                                });
                            }
                        } else {
                            // User needs to verify to continue
                            DialogService.openUniVerifyDialog({
                                payload: {
                                    checkConnections: true,
                                    // TODO: FIX DIS TEXT
                                    helperText: i18n.t(
                                        'general.dialogs.helperTexts.verifySchoolConnection'
                                    ),
                                },
                                then: payload => {
                                    if (payload instanceof Object && payload.isVerified) {
                                        next();
                                    }
                                },
                            });
                        }
                    });
            } else {
                next();
            }
        }
    } else {
        next(); // Always have to call next()
    }
});

router.afterEach(to => {
    // Log pageviews. Pages that are just an redirect from external token-providers
    // like /redirect will not be logged. Same with /recover and /recover-password
    // because they contain sensitive data in the url-query.
    if (
        !to.path.startsWith(URLS.redirect) &&
        !to.path.startsWith(URLS.recoverUser) &&
        !to.path.startsWith(URLS.recoverPassword)
    ) {
        AnalyticService.logPageview(to.path);
    }

    if (localization.isSchoolPlatform) {
        RestX.getInterfaces().university.connection.get({ forceUpdate: true });
    }

    const logInModule = RestX.getInterfaces().logInModule;
    if (logInModule.shouldOpenOnNewPage()) {
        logInModule.setHelperText(i18n.t('general.dialogs.helperTexts.logInToAccessMoreFeatures'));
        logInModule.setOpen(true);
        logInModule.setShouldOpenOnNewPage(false);
    }
});

export default router;
